/**
 * Streak Calendar illustration — miniature wireframe of a weekly streak grid:
 * 7-day row with active/inactive states, streak count, flame accent.
 */
import type { IllustrationProps } from './types';
import { usePalette } from './types';

export function StreakCalendar({ variant = 'on-gradient', className, style }: IllustrationProps) {
  const p = usePalette(variant);

  // Which days are "active" in the illustration
  const days = [true, true, true, false, true, true, false];

  return (
    <svg
      viewBox="0 0 120 80"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      style={style}
      aria-hidden="true"
    >
      {/* Card frame */}
      <rect
        x="4"
        y="4"
        width="112"
        height="72"
        rx="10"
        fill={p.frameFill}
        stroke={p.frameStroke}
        strokeWidth="1"
      />

      {/* Streak hero — number + flame */}
      <rect x="14" y="12" width="20" height="8" rx="2" fill={p.strong} opacity="0.2" />
      {/* Flame shape */}
      <path
        d="M40 11 C40 11, 42 14, 42 16 C42 17.5, 41 18.5, 40 18.5 C39 18.5, 38 17.5, 38 16 C38 14, 40 11, 40 11Z"
        fill={p.accent1}
        opacity="0.6"
      />
      {/* "day streak" label */}
      <rect x="14" y="23" width="26" height="2.5" rx="1.25" fill={p.faint} />

      {/* 7-day calendar grid */}
      {days.map((active, i) => {
        const x = 14 + i * 14;
        return (
          <g key={i}>
            {/* Day circle */}
            <circle
              cx={x + 5}
              cy={48}
              r="5.5"
              fill={active ? p.accent1 : p.faint}
              opacity={active ? 0.5 : 1}
            />
            {/* Check mark for active days */}
            {active && (
              <path
                d={`M${x + 3} 48 L${x + 4.5} 49.5 L${x + 7} 46.5`}
                stroke={p.strong}
                strokeWidth="1"
                strokeLinecap="round"
                strokeLinejoin="round"
                opacity="0.5"
              />
            )}
            {/* Day label below — represented as tiny rect */}
            <rect x={x + 3} y="58" width="4" height="2" rx="1" fill={p.faint} />
          </g>
        );
      })}

      {/* Streak count on right side */}
      <rect x="86" y="44" width="22" height="6" rx="3" fill={p.accent1} opacity="0.3" />
      <rect x="90" y="46" width="14" height="2" rx="1" fill={p.accent1} opacity="0.6" />
    </svg>
  );
}
